import {USED_APPEARANCE_ROLES} from './appearance-roles';

export type BaseScheme = 'light' | 'dark';
export type PaletteColors = Record<string, string>;
export interface AppearancePaletteDto {id: string; name: string; baseScheme: BaseScheme; colors: PaletteColors; isStandard: boolean}
export interface AppearanceCatalogDto {schemaVersion: number; palettes: AppearancePaletteDto[]}
export interface AppearancePalette extends AppearancePaletteDto {standard: boolean}
export interface SaveAppearancePaletteDto {name: string; baseScheme: BaseScheme; colors: PaletteColors}

export const APPEARANCE_GROUPS = ['canvas', 'header', 'breadcrumbs', 'navigation', 'quickNavigation', 'footer', 'surface', 'table',
  'primary', 'secondary', 'danger', 'priorityHigh', 'priorityMedium', 'priorityLow', 'iconButton', 'input', 'fieldHint', 'photoFrame', 'photoMain', 'photoAi', 'dropdown', 'calendar', 'checkbox', 'radio', 'toggle',
  'tabs', 'dialog', 'menu', 'tooltip', 'success', 'warning', 'error', 'info', 'search', 'loading', 'empty', 'scrollbar', 'profile', 'widget', 'card', 'changeAdded', 'changeUpdated', 'media', 'scanButton'] as const;
export const APPEARANCE_STATES = ['normal', 'hover', 'pressed', 'focus', 'selected', 'disabled', 'error'] as const;
export const APPEARANCE_PROPERTIES = ['background', 'backgroundEnd', 'text', 'muted', 'icon', 'border', 'shadow', 'backdrop'] as const;
export const APPEARANCE_SCHEMA = 1;

export function groupStates(group: string): readonly string[] {
  return APPEARANCE_STATES.filter(state => USED_APPEARANCE_ROLES.some(role => role.startsWith(`${group}.${state}.`)));
}

export const PALETTE_ROLES: readonly string[] = USED_APPEARANCE_ROLES;

const QUICK_NAVIGATION_COLORS: Record<BaseScheme, string[]> = {
  light: ['#ddecfc', '', '#327fc1', '', '', '#397eca', '', ''],
  dark: ['#35465f', '', '#e6edf7', '', '', '#8ec8ff', '', ''],
};
const PHOTO_AND_HINT_COLORS: Record<BaseScheme, Record<string, string[]>> = {
  light: {
    'fieldHint.normal': ['', '', '#52647c', '', '', '', '', ''],
    'photoFrame.selected': ['', '', '', '', '', '#2a76d2', '', ''],
    'photoMain.normal': ['#ffffff', '', '', '', '#2b507c', '#6686aa', '', ''],
    'photoMain.selected': ['#173c70', '', '', '', '#ffffff', '#ffffff', '', ''],
    'photoAi.normal': ['#ffffff', '', '', '', '#65459e', '#8a76b0', '', ''],
    'photoAi.selected': ['#50349f', '', '', '', '#ffffff', '#e1d6ff', '', ''],
  },
  dark: {
    'fieldHint.normal': ['', '', '#c4d2e6', '', '', '', '', ''],
    'photoFrame.selected': ['', '', '', '', '', '#8fc6ff', '', ''],
    'photoMain.normal': ['#23344b', '', '', '', '#d9eaff', '#93aac4', '', ''],
    'photoMain.selected': ['#315f94', '', '', '', '#ffffff', '#b9ddff', '', ''],
    'photoAi.normal': ['#2e2945', '', '', '', '#e2d6ff', '#aa9dc8', '', ''],
    'photoAi.selected': ['#6753b9', '', '', '', '#ffffff', '#d3c3ff', '', ''],
  },
};

// Presets contain independent values. Editing a role never derives or changes another role.
export function baseColors(base: BaseScheme): PaletteColors {
  const dark = base === 'dark';
  const colors: PaletteColors = {};
  for (const group of APPEARANCE_GROUPS) {
    for (const state of groupStates(group)) {
      const values = PHOTO_AND_HINT_COLORS[base][`${group}.${state}`]
        ?? (group === 'quickNavigation' ? QUICK_NAVIGATION_COLORS[base] : presetState(group, state, dark));
      APPEARANCE_PROPERTIES.forEach((property, index) => {
        const role = `${group}.${state}.${property}`;
        if (PALETTE_ROLES.includes(role)) colors[role] = values[index];
      });
    }
  }
  return colors;
}

function presetState(group: string, state: string, dark: boolean): string[] {
  const priorityIcons: Record<string, string> = {
    priorityHigh: dark ? '#ff9b9b' : '#b42318',
    priorityMedium: dark ? '#91dfb7' : '#15803d',
    priorityLow: dark ? '#8ec8ff' : '#327fc1',
  };
  if (priorityIcons[group]) return ['', '', '', '', priorityIcons[group], '', '', ''];
  if (group === 'scanButton') return [dark ? '#4da79e' : '#1e746e', dark ? '#4da79e' : '#1e746e', dark ? '#112033' : '#ffffff', '#ffffff', '#ffffff', '#ffffff00', '#00000000', '#00000000'];
  if (!dark && group === 'header') return ['#f1f5faf2', '#f1f5faf2', '#27405d', '#5e7289', '#20324d', '#c7d2e1f5', '#00000000', '#00000000'];
  if (!dark && group === 'breadcrumbs') return [state === 'selected' ? '#dfe4ff' : state === 'hover' ? '#eff6ff' : '#ffffff', '#ffffff', state === 'selected' ? '#3250a2' : state === 'hover' ? '#2563eb' : '#334155', '#64748b', '#334155', '#d8e0ee', '#00000000', '#f5f7fb'];
  if (group === 'changeAdded' || group === 'changeUpdated') {
    const added = group === 'changeAdded';
    return dark
      ? [added ? '#443820' : '#233c59', added ? '#382e1d' : '#1c3049', added ? '#f7d387' : '#aed5ff', '#b1bfd2', '#aed5ff', '#465570', '#00000033', '#ffffff0a']
      : [added ? '#fff2df' : '#edf3fb', added ? '#f6e0be' : '#dce8f7', added ? '#9a6700' : '#3c5d88', '#62778e', '#3c5d88', '#6482a838', '#38526e14', '#ffffffcc'];
  }
  if (group === 'media') return dark
    ? ['#29384d', '#29384d', '#b1bfd2', '#b1bfd2', '#b1bfd2', '#465570', '#00000000', '#00000000']
    : ['#e9f0f7', '#e9f0f7', '#4d6685', '#4d6685', '#4d6685', '#dbe6f1', '#00000000', '#00000000'];
  if (group === 'profile') {
    return dark
      ? ['#354d70', '#253650', '#e6edf7', '#b1bfd2', '#fefefe', '#465570', '#ffffff33', '#00000000']
      : ['#d7e6fb', '#b9cff1', '#203149', '#5a6d83', '#fefefe', '#b6c2d1f5', '#ffffffb3', '#00000000'];
  }
  if (group === 'scrollbar') {
    const track = dark ? '#202b3c' : '#d8e0eae6';
    const thumb = dark ? '#8294ad' : '#7a8a9ef2';
    return [track, track, thumb, thumb, thumb, track, '#00000000', '#00000000'];
  }
  let background = dark ? '#202b3c' : '#ffffff';
  let text = dark ? '#e6edf7' : '#263954';
  let border = dark ? '#465570' : '#c7d4e6';
  if (['canvas', 'header', 'navigation', 'footer'].includes(group)) background = dark ? '#151d2b' : '#edf2f8';
  if (['hover', 'focus', 'selected', 'pressed'].includes(state)) background = dark ? '#35465f' : '#e3edf9';
  if (group === 'primary') { background = dark ? '#75b7ee' : '#2f7ec4'; text = dark ? '#112033' : '#ffffff'; }
  if (group === 'danger') { background = dark ? '#f19b9b' : '#ae3434'; text = dark ? '#301416' : '#ffffff'; }
  const semantic: Record<string, string[]> = {
    success: dark ? ['#183d32', '#91dfb7'] : ['#edf9f2', '#176a48'],
    warning: dark ? ['#443820', '#f7d387'] : ['#f6e0c8', '#b46a1c'],
    error: dark ? ['#47262c', '#ffb5b5'] : ['#f2d8d7', '#b14b46'],
    info: dark ? ['#233c59', '#aed5ff'] : ['#d9e6f7', '#355d9d'],
  };
  if (semantic[group]) [background, text] = semantic[group];
  if (state === 'disabled') { background = dark ? '#293242' : '#e6ebf1'; text = dark ? '#98a4b8' : '#708096'; }
  if (state === 'error') border = dark ? '#f19b9b' : '#b42318';
  if (state === 'focus') border = dark ? '#8ec8ff' : '#397eca';
  if (group === 'warning') border = dark ? '#d9aa55' : '#f4b24ad1';
  if (group === 'error') border = dark ? '#d98b8b' : '#dd6363d1';
  if (group === 'info') border = dark ? '#75b7ee' : '#3083ffd1';
  if (!dark && group === 'widget') return ['#fffffffa', '#fafcfff0', '#102033', '#102033b3', '#17314c', '#829cbc38', '#2a43651a', '#ffffffd6'];
  if (!dark && group === 'card') {
    return ['#fbfdff', '#f4f8fc', '#18355a', '#6f839d', '#4d6685', state === 'hover' ? '#cad8e7' : '#dbe6f1', state === 'hover' ? '#2c436014' : '#2c43600d', '#ffffffd9'];
  }
  if (!dark && group === 'tabs') {
    const selected = state === 'selected';
    return [selected ? '#edf1ff' : '#ffffff', '#ffffff', selected ? '#263e8c' : '#3e5f8a', '#697991', '#3e5f8a', state === 'focus' ? '#4158d0' : selected ? '#7e91d6' : '#dbe6f1', '#00000000', '#00000000'];
  }
  if (!dark && group === 'secondary') return ['#fffffff0', '#fffffff0', '#2a80c5', '#697991', '#2a80c5', '#b4c1d1c7', '#41546f14', '#ffffffd9'];
  let backgroundEnd = background;
  let backdrop = '#00000085';
  if (dark && ['widget', 'card', 'secondary'].includes(group)) backdrop = '#ffffff0a';
  if (dark && group === 'breadcrumbs') backdrop = '#202b3c';
  if (group === 'canvas' && !dark) { background = '#f7fbff'; backgroundEnd = '#eef4fb'; }
  if (group === 'navigation' && state === 'normal') {
    background = dark ? '#1c283a' : '#f0f4fa';
    backgroundEnd = dark ? '#151d2b' : '#e6edf5';
    backdrop = dark ? '#48618140' : '#cdd9e9c2';
  }
  if (group === 'navigation' && !dark) {
    text = '#2f4e7c';
    if (['hover', 'focus', 'selected', 'pressed'].includes(state)) {
      background = '#d6e4f6f5';
      backgroundEnd = background;
    }
  }
  const muted = group === 'navigation' && !dark ? '#718195' : dark ? '#b1bfd2' : '#697991';
  const icon = group === 'navigation' && !dark ? '#18355a' : text;
  return [background, backgroundEnd, text, muted, icon, border,
    dark ? '#00000055' : '#45587d20', backdrop];
}

export function standardPalette(baseScheme: BaseScheme): AppearancePalette {
  return {id: baseScheme, name: baseScheme, baseScheme, colors: baseColors(baseScheme), standard: true, isStandard: true};
}

export function catalogPalettes(palettes: readonly AppearancePaletteDto[]): AppearancePalette[] {
  const mapped = palettes.map(palette => ({...palette,
    colors: normalizeColors(palette.baseScheme, palette.colors), standard: palette.isStandard}));
  const standards = (['light', 'dark'] as const).map(base =>
    mapped.find(palette => palette.standard && palette.baseScheme === base) ?? standardPalette(base));
  return [...standards, ...mapped.filter(palette => !palette.standard)];
}

export function normalizeColors(base: BaseScheme, colors: PaletteColors): PaletteColors {
  const normalized = baseColors(base);
  for (const role of PALETTE_ROLES) {
    if (validColor(colors[role])) normalized[role] = colors[role];
  }
  return normalized;
}

export function validColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[\da-f]{6}([\da-f]{2})?$/i.test(value);
}

export function validColors(value: unknown): value is PaletteColors {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  return PALETTE_ROLES.every(role => validColor((value as PaletteColors)[role]))
    && entries.every(([role, color]) => PALETTE_ROLES.includes(role) && validColor(color));
}

export function validCatalog(value: unknown): value is AppearanceCatalogDto {
  if (!value || typeof value !== 'object' || !('schemaVersion' in value) || !('palettes' in value)) return false;
  return value.schemaVersion === APPEARANCE_SCHEMA && Array.isArray(value.palettes) && value.palettes.every(validPalette);
}

export function validPalette(value: unknown): value is AppearancePaletteDto {
  if (!value || typeof value !== 'object') return false;
  const palette = value as Partial<AppearancePaletteDto>;
  return typeof palette.id === 'string' && typeof palette.name === 'string'
    && typeof palette.isStandard === 'boolean'
    && (palette.baseScheme === 'light' || palette.baseScheme === 'dark')
    && !!palette.colors && typeof palette.colors === 'object' && !Array.isArray(palette.colors)
    && Object.keys(palette.colors).length > 0 && Object.values(palette.colors).every(validColor);
}

export function paletteStyles(colors: PaletteColors): Record<string, string> {
  const styles: Record<string, string> = {};
  for (const role of PALETTE_ROLES) {
    if (validColor(colors[role])) styles[`--ap-${role.replace(/\./g, '-')}`] = colors[role];
  }
  return styles;
}

export function cloneName(source: string, names: readonly string[], suffix: string): string {
  const normalized = new Set(names.map(name => name.trim().normalize('NFKC').toUpperCase()));
  let number = 1;
  let name: string;
  do {
    const ending = ` — ${suffix}${number === 1 ? '' : ` ${number}`}`;
    name = source.slice(0, 100 - ending.length) + ending;
    number++;
  } while (normalized.has(name.normalize('NFKC').toUpperCase()));
  return name;
}
